import { useMemo, useState } from 'react';
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Typography, type SxProps, type Theme } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { useFeatureGate } from '@/hooks/useFeatureGate';
import { useRolePinGroups, withRolePins } from '@/hooks/useHomeLayout';
import { navGroupsFor, type NavGroup, type NavItem } from './navConfig';
import { useWorkspace } from '@/stores/WorkspaceStore';

/**
 * Horizontal workspace navigation for the Top Navigation layout. Each workspace is a dropdown of its
 * permitted menus; a workspace with no menus (Dashboard) is a plain link. Submenus are flattened into
 * labelled sections so the dropdown stays one level deep. Reuses navGroups, the permission engine, icons
 * and active-route logic — no menu is hardcoded. Overflows into a horizontally-scrollable row so it never
 * breaks the page layout; mobile uses the drawer instead.
 */
export default function ModuleNav({ sx, onNavigate }: { sx?: SxProps<Theme>; onNavigate?: () => void }) {
  const { hasAnyPermission } = useAuth();
  const { isFeatureEnabled } = useFeatureGate();
  const { workspace } = useWorkspace();
  const { pathname } = useLocation();
  const [anchor, setAnchor] = useState<{ el: HTMLElement; group: NavGroup } | null>(null);
  const rolePins = useRolePinGroups();

  // Same recursive permission/feature filtering the sidebar applies; RBAC itself is unchanged.
  const groups = useMemo(() => {
    const allowed = (item: NavItem) => (!item.perms || hasAnyPermission(item.perms)) && isFeatureEnabled(item.feature);
    const filterItems = (items: NavItem[]): NavItem[] => items.reduce<NavItem[]>((acc, item) => {
      if (item.children?.length) {
        const children = filterItems(item.children);
        if (children.length) acc.push({ ...item, children });
      } else if (item.to && allowed(item)) {
        acc.push(item);
      }
      return acc;
    }, []);

    const visible = navGroupsFor(workspace).reduce<NavGroup[]>((acc, group) => {
      if (group.to) {
        if (allowed(group as NavItem)) acc.push(group);
        return acc;
      }
      const children = filterItems(group.children ?? []);
      if (children.length) acc.push({ ...group, children });
      return acc;
    }, []);
    // FR-NAV-03/04: the role's pinned daily items right after Home; Masters and Settings stay last.
    return withRolePins(visible, rolePins);
  }, [hasAnyPermission, isFeatureEnabled, workspace, rolePins]);

  const isRouteActive = (to?: string) => !!to && (pathname === to || pathname.startsWith(`${to}/`));

  const activeGroupLabel = useMemo(() => groups.find((g) => (g.to
    ? isRouteActive(g.to) || (g.activeOn ?? []).some((p) => isRouteActive(p))
    : (g.children ?? []).some((c) => (c.children?.length
      ? c.children.some((s) => isRouteActive(s.to))
      : isRouteActive(c.to)))))?.label ?? null,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [groups, pathname]);

  const buttonSx = (isActive: boolean) => ({
    flexShrink: 0, color: isActive ? 'primary.main' : 'text.secondary', fontWeight: isActive ? 700 : 600,
    fontSize: 13.5, textTransform: 'none', borderRadius: 2, px: 1.25, whiteSpace: 'nowrap',
    bgcolor: isActive ? (t: Theme) => alpha(t.palette.primary.main, 0.1) : 'transparent',
    '&:hover': { bgcolor: (t: Theme) => alpha(t.palette.primary.main, 0.08) },
  });

  return (
    <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', ...sx }}>
      {groups.map((group) => {
        const isActive = group.label === activeGroupLabel;

        // Workspace with no menus is a direct link.
        if (group.to) {
          return (
            <Button key={group.label} component={RouterLink} to={group.to} onClick={onNavigate}
              startIcon={group.icon} sx={buttonSx(isActive)}>
              {group.label}
            </Button>
          );
        }

        return (
          <Button
            key={group.label}
            onClick={(e) => setAnchor({ el: e.currentTarget, group })}
            startIcon={group.icon}
            endIcon={<ExpandMore sx={{ fontSize: 16, opacity: 0.6 }} />}
            aria-haspopup="true"
            aria-expanded={anchor?.group.label === group.label}
            sx={buttonSx(isActive)}
          >
            {group.label}
          </Button>
        );
      })}

      <Menu
        anchorEl={anchor?.el ?? null}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 240, borderRadius: 3, maxHeight: '70vh' } } }}
      >
        {(anchor?.group.children ?? []).flatMap((item) => {
          if (!item.children?.length) {
            const active = isRouteActive(item.to);
            return [(
              <MenuItem key={item.to} component={RouterLink} to={item.to!} selected={active}
                onClick={() => { setAnchor(null); onNavigate?.(); }}>
                {item.icon && <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>}
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 700 : 500 }} />
              </MenuItem>
            )];
          }
          // Submenu -> labelled section, keeping the dropdown flat.
          return [
            <Typography key={`h-${item.label}`} variant="caption"
              sx={{ px: 2, pt: 1, pb: 0.25, display: 'block', color: 'text.secondary', fontWeight: 700 }}>{item.label}</Typography>,
            ...item.children.map((child) => {
              const active = isRouteActive(child.to);
              return (
                <MenuItem key={child.to} component={RouterLink} to={child.to!} selected={active}
                  onClick={() => { setAnchor(null); onNavigate?.(); }}>
                  {child.icon && <ListItemIcon sx={{ minWidth: 34 }}>{child.icon}</ListItemIcon>}
                  <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 700 : 500 }} />
                </MenuItem>
              );
            }),
          ];
        })}
      </Menu>
    </Box>
  );
}
